/**
 * Research Content: Overnights and Parenting Plans
 *
 * Evidence-based summary of peer-reviewed research on overnight stays
 * for young children in shared parenting arrangements.
 *
 * Content requirements: 1,500+ words original editorial, 5+ sources,
 * no more than 2 citations from a single author (FR-027, FR-028, FR-029).
 */

import type { ResearchPageData } from './types'

export const overnightsAndParentingPlans: ResearchPageData = {
  slug: 'overnights-and-parenting-plans',
  heading:
    'What Does the Research Say About Overnights for Young Children in Shared Parenting Plans?',
  metaTitle: 'Overnights & Parenting Plans Research | NCESP',
  metaDescription:
    'What does the research say about overnights for young children in shared parenting plans? Evidence-based summary of peer-reviewed studies.',
  keywords: [
    'overnights young children',
    'parenting plans research',
    'shared custody overnights',
    'Warshak overnight study',
    'equal shared parenting evidence',
  ],
  datePublished: '2026-03-10',
  dateModified: '2026-03-10',
  breadcrumbLabel: 'Overnights & Parenting Plans',

  introduction: [
    'One of the most emotionally charged questions in custody proceedings is whether young children should spend overnights with both parents. For decades, family courts relied heavily on the assumption that very young children, particularly those under four, needed one primary home and should not spend nights away from a single "primary attachment figure." This assumption shaped countless custody decisions and restricted many loving parents from meaningful time with their own children.',
    'In recent years, however, a growing body of peer-reviewed research has challenged that assumption. Leading developmental psychologists, family researchers, and child welfare experts have examined the evidence and found that the traditional restriction on overnights is not supported by the science. In fact, the research increasingly shows that children benefit from regular, frequent contact with both parents, including overnights, from an early age.',
    'This page summarizes the key research findings on overnights and parenting plans, presents the evidence in context, and explains what it means for families navigating custody decisions. All findings are paraphrased from published, peer-reviewed studies and are presented in language accessible to parents, advocates, and policymakers alike.',
  ],

  sections: [
    {
      heading: 'The Key Question',
      body: [
        'At the heart of the overnight debate is a fundamental question: Does spending nights with both parents harm young children, or does it help them? For years, some mental health professionals argued that overnights disrupted the primary attachment bond (typically with the mother) and caused emotional distress in young children. This position was largely influenced by a single theoretical framework that prioritized one parent-child bond over all others.',
        'However, attachment research has evolved considerably since those early theories. Modern developmental science recognizes that children routinely form multiple secure attachments to mothers, fathers, grandparents, and other caregivers, and that these attachments develop through regular, meaningful interaction. Overnight stays are one of the most natural and powerful ways for young children to build and sustain attachment relationships with both parents.',
      ],
      keyFindings: [],
    },
    {
      heading: 'What the Research Shows',
      body: [
        'Over the past two decades, several large-scale empirical studies have directly examined the outcomes of children who spend overnights with both parents compared to those who do not. The findings are remarkably consistent: overnight parenting time does not harm children and is often associated with better outcomes.',
      ],
      keyFindings: [
        {
          statement:
            'Children with regular overnights show equal or better adjustment than those without',
          context:
            'A landmark study by William Fabricius and colleagues followed over 100 young adults and found that those who had overnight stays with their fathers as young children enjoyed closer relationships with their fathers in adulthood, without any negative effect on their relationship with their mothers.',
          sourceId: 1,
        },
        {
          statement:
            'No evidence that overnights harm children under four years old',
          context:
            'A comprehensive review of the scientific literature by a panel of 110 researchers and practitioners concluded that the consensus of the evidence does not support restricting overnights for young children. Blanket age-based restrictions lack empirical justification.',
          sourceId: 2,
        },
        {
          statement:
            'Frequent contact with both parents supports secure attachment',
          context:
            'Research in developmental psychology demonstrates that attachment security develops through consistent, responsive caregiving over time. Regular overnight contact gives children the opportunity to experience the full rhythm of daily life (meals, bedtime routines, morning rituals) with each parent.',
          sourceId: 3,
        },
        {
          statement:
            'Restricting overnights may weaken the father-child relationship',
          context:
            'Studies show that when overnights are restricted, the noncustodial parent, most often the father, has significantly less opportunity for the routine caregiving interactions that build secure attachment. Over time, this can lead to a weaker parent-child bond and increased risk of paternal disengagement.',
          sourceId: 4,
        },
        {
          statement:
            'Shared parenting arrangements are associated with better child well-being',
          context:
            'A meta-analysis encompassing data from multiple countries found that children in joint physical custody arrangements reported better well-being across emotional, behavioral, and academic measures compared to children in sole custody. The difference held even after controlling for parental conflict.',
          sourceId: 5,
        },
      ],
    },
    {
      heading: 'What This Means for Families',
      body: [
        "The research has clear implications for families and for the courts that serve them. The traditional approach of restricting overnight parenting time for young children lack empirical support and may in fact cause harm by weakening the child's relationship with one parent. Equal shared parenting arrangements that include regular overnights allow children to maintain strong, secure bonds with both parents.",
        'For parents navigating custody decisions, the evidence supports creating parenting plans that include overnight time with both parents from an early age, provided that both parents are fit and willing caregivers. The research does not suggest that every family situation is identical; individual circumstances always matter. But the scientific consensus is clear: the default starting point should be shared time, not restrictions.',
        "For policymakers and judges, the research underscores the need to update legal presumptions and custody guidelines to reflect current scientific understanding. Blanket restrictions on overnight parenting time based solely on a child's age are not evidence-based and should be reconsidered in favor of individualized assessments that prioritize the child's relationship with both parents.",
        'NCESP advocates for custody policies informed by the best available research. We believe that when both parents are fit and willing, children deserve the opportunity to develop and maintain meaningful relationships with each of them. The evidence shows that shared parenting, including overnights, supports that goal.',
      ],
      keyFindings: [],
    },
  ],

  expertCallout: {
    quote:
      'Social science research does not support policies that categorically restrict overnight contact between young children and their parents. The evidence shows that overnights are important for the development of healthy parent-child relationships.',
    expertName: 'Richard A. Warshak, Ph.D.',
    credentials:
      'Clinical Professor of Psychiatry, University of Texas Southwestern Medical Center',
    linkUrl:
      'https://www.warshak.com/complimentary-articles/#parenting-plans-overnights',
    linkText: "Read Dr. Warshak's full analysis",
  },

  faqItems: [
    {
      question:
        'Are overnight stays harmful for infants and toddlers in shared custody?',
      answer:
        'No. The consensus of the peer-reviewed research is that overnight stays with both parents are not harmful to infants and toddlers when both parents provide safe, responsive caregiving. Studies have found that young children who experience regular overnights with both parents develop secure attachments to each parent without negative effects. What matters most is the quality and consistency of care, not preserving a single-home arrangement.',
    },
    {
      question:
        "Didn't some researchers argue that overnights hurt young children?",
      answer:
        'A small number of early studies raised concerns about overnights for very young children, but these studies have been widely critiqued for methodological limitations including small sample sizes, selection bias, and failure to control for parental conflict. Subsequent large-scale studies with stronger methodologies have consistently found no harm from overnight parenting arrangements. A consensus statement signed by 110 experts in 2014 specifically addressed and rejected the earlier claims as unsupported by the weight of the evidence.',
    },
    {
      question:
        'What does "secure attachment" mean, and how do overnights support it?',
      answer:
        "Secure attachment describes a child's confidence that a caregiver will be available, responsive, and supportive. It develops through repeated, consistent caregiving interactions over time: feeding, comforting, bathing, playing, and putting the child to sleep. Overnight stays give both parents the opportunity to engage in these routine caregiving activities, which is essential for building and maintaining secure attachment with each parent.",
    },
    {
      question:
        'Do high levels of parental conflict change the recommendations about overnights?',
      answer:
        'High parental conflict is harmful to children regardless of the custody arrangement. However, research indicates that the custody structure itself, whether sole or shared, is not the primary driver of conflict-related harm. Shared parenting does not increase conflict, and some evidence suggests it may reduce conflict over time by reducing perceptions of unfairness. The presence of conflict is not a scientific justification for restricting overnights when both parents are otherwise fit caregivers.',
    },
    {
      question:
        'What professional organizations support shared parenting with overnights?',
      answer:
        'The Association of Family and Conciliation Courts (AFCC) published guidelines supporting individualized parenting plans that include overnights for young children. The American Psychological Association has published research acknowledging the importance of both parent-child relationships. In 2014, 110 researchers and practitioners signed a consensus report affirming that social science research does not support blanket restrictions on overnight parenting time for young children.',
    },
    {
      question:
        'How does this research apply to my specific custody situation?',
      answer:
        "The research provides general guidance based on large samples and aggregate outcomes. It establishes that overnight parenting time is not inherently harmful and is generally beneficial. However, every family's circumstances are unique. The research supports the principle that shared parenting including overnights should be the starting presumption, with individualized adjustments based on specific factors such as parental fitness, geographic proximity, and the child's individual needs. Consulting a family law professional familiar with current research is always advisable.",
    },
  ],

  sourceCitations: [
    {
      id: 1,
      authors: 'Fabricius, W. V., & Suh, G. W.',
      title:
        'Should infants and toddlers have frequent overnight parenting time with fathers? The policy debate and new data',
      publication: 'Psychology, Public Policy, and Law',
      year: 2017,
      url: 'https://doi.org/10.1037/law0000108',
    },
    {
      id: 2,
      authors: 'Warshak, R. A.',
      title:
        'Social science and parenting plans for young children: A consensus report',
      publication: 'Psychology, Public Policy, and Law',
      year: 2014,
      url: 'https://doi.org/10.1037/law0000005',
    },
    {
      id: 3,
      authors: 'Lamb, M. E.',
      title:
        "Mothers, fathers, families, and circumstances: Factors affecting children's adjustment",
      publication: 'Applied Developmental Science',
      year: 2012,
      url: 'https://doi.org/10.1080/10888691.2012.667344',
    },
    {
      id: 4,
      authors: 'Pruett, M. K., McIntosh, J. E., & Kelly, J. B.',
      title:
        'Parental separation and overnight care of young children, part I: Consensus through theoretical and empirical integration',
      publication: 'Family Court Review',
      year: 2014,
    },
    {
      id: 5,
      authors: 'Nielsen, L.',
      title:
        'Joint versus sole physical custody: Outcomes for children independent of family income or parental conflict',
      publication: 'Journal of Child Custody',
      year: 2018,
      url: 'https://doi.org/10.1080/15379418.2017.1422414',
    },
    {
      id: 6,
      authors:
        'Tornello, S. L., Emery, R., Rowen, J., Potter, D., Ocker, B., & Xu, Y.',
      title:
        'Overnight custody arrangements, attachment, and adjustment among very young children',
      publication: 'Journal of Marriage and Family',
      year: 2013,
      url: 'https://doi.org/10.1111/jomf.12045',
    },
  ],

  relatedPages: [
    {
      title: 'Equal Shared Parenting',
      path: '/issues/equal-shared-parenting',
      description:
        'Learn about the case for equal shared parenting as the default custody presumption and how it benefits children.',
    },
    {
      title: 'About NCESP',
      path: '/about',
      description:
        "Learn about the National Council for Equal Shared Parenting's mission, history, and advocacy efforts.",
    },
  ],

  ctaBlock: {
    heading: 'Support Evidence-Based Parenting Policy',
    body: 'Stay informed about the latest research on shared parenting and join the movement to reform family court policies. Subscribe for updates or donate to support our mission.',
    actions: [
      { label: 'Subscribe for Updates', action: 'subscribe' },
      { label: 'Donate Now', action: 'donate' },
    ],
  },
}
